import { FinalReviewSample } from "vl-api/vlSchemas";
import AddTagIcon from "assets/icons/AddTagIcon";
import IgnoreIcon from "assets/icons/FloatIcons/IgnoreIcon";
import { Option } from "views/uikit/FormFields/SelectList";
import { FloatingDetails } from "views/components/LabelPropagation/ReviewFloatingDetails";
import { ActionItem, Preview } from "../DataList";
import { ActionType } from "../ReviewList/types";
import { FINAL_REVIEW_UNLABELED } from "helpers/constants/miscellaneous";

export const UNLABELED_NAME = "Unlabeled";

export const FINAL_REVIEW_TOOLTIPS: Record<
  ActionType,
  { reassign: string; ignore: string }
> = {
  SINGLE: {
    reassign: "Reassign: Move this image to the correct label",
    ignore: "Ignore: Move this image to Unlabeled. It will not get a label",
  },
  BULK: {
    reassign: "Reassign: Move these images to the correct label",
    ignore: "Ignore: Move these images to Unlabeled. They will not get a label",
  },
};

export const formatConfidence = (confidence: number) =>
  `${Math.round(confidence <= 1 ? confidence * 100 : confidence)}% confident`;

/** The model confidence of images not changed in the final review (ignored images are changed too) */
export const getSampleFloatingDetails = (
  sample: FinalReviewSample,
): FloatingDetails | undefined => {
  if (sample.changed_in_final_review) {
    return undefined;
  }
  if (sample.confidence !== null && sample.confidence !== undefined) {
    return { type: "default", value: formatConfidence(sample.confidence) };
  }
  return undefined;
};

interface CreateActionListParams {
  actionType: ActionType;
  getSampleIds: () => string[];
  reassignOptions: Option[];
  onReassign: (sampleIds: string[], labelId: string) => unknown;
  onIgnore?: (sampleIds: string[]) => unknown;
}

/** Final review actions: reassign (to another label or Unlabeled) and ignore */
export const createFinalReviewActionList = ({
  actionType,
  getSampleIds,
  reassignOptions,
  onReassign,
  onIgnore,
}: CreateActionListParams): ActionItem[] => [
  {
    action: {
      text: "reassign",
      icon: <AddTagIcon />,
      type: "primary",
      color: "primary",
      onClick: () => {},
    },
    tooltip: FINAL_REVIEW_TOOLTIPS[actionType].reassign,
    subAction: {
      options: reassignOptions,
      onChange: (selectedIds: string[]) => {
        if (selectedIds.length) {
          onReassign(getSampleIds(), selectedIds.toString());
        }
      },
      open: true,
    },
  },
  ...(onIgnore
    ? [
        {
          action: {
            text: "ignore",
            icon: <IgnoreIcon />,
            type: "primary" as const,
            color: "warning",
            onClick: () => {
              onIgnore(getSampleIds());
            },
          },
          tooltip: FINAL_REVIEW_TOOLTIPS[actionType].ignore,
        },
      ]
    : []),
];

/** The class of a sample in the final review: its label, or the virtual Unlabeled class once ignored */
export const getSampleClassId = (sample: FinalReviewSample) =>
  sample.ignored ? FINAL_REVIEW_UNLABELED : sample.label_category_id;

export const toFinalReviewPreview = (
  sample: FinalReviewSample,
  actionList: ActionItem[],
  {
    onDetailsClick = () => {},
  }: {
    onDetailsClick?: (preview: Preview) => void;
  } = {},
): Preview => {
  const changed = !!sample.changed_in_final_review;
  return {
    media_id: sample.id,
    image_id: sample.id,
    relevanceType: null,
    media_uri: sample.image_uri,
    type: "IMAGE",
    title: sample.original_uri,
    opacity: sample.ignored ? 50 : 100,
    floatingDetails: getSampleFloatingDetails(sample),
    label: {
      name: sample.ignored ? UNLABELED_NAME : sample.label || "",
      type: changed || sample.source === "REVIEW" ? "User" : "VL",
    },
    actionList,
    onDetailsClick,
    completed: true,
    labelId: getSampleClassId(sample),
  };
};
